<script setup lang="ts">
import type { GaiaFileMeta } from '@/api';

import { computed, ref, watch } from 'vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaCheckbox from '@/components/design/base/GaiaCheckbox.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import { gaiaAccent, gaiaNeutral } from '@/components/design/themes/tokens';

const props = defineProps({
  gaiaStore: { type: Object, required: true },
  selectedFileKeys: { type: Array as () => string[], required: true },
  totalSelectedCount: { type: Number, default: 0 },
});

const emit = defineEmits(['selection-change']);

const expandedSections = ref<Set<string>>(new Set());
const localSelectedKeys = ref<string[]>([]);

// Initialize local selection from props
watch(
  () => props.selectedFileKeys,
  newKeys => {
    const gaiaKeys = newKeys.filter(key => key.startsWith('gaia-'));
    localSelectedKeys.value = gaiaKeys;
  },
  { immediate: true }
);

const hasGaiaFilesSelected = computed((): boolean => {
  return localSelectedKeys.value.length > 0;
});

function fileKey(id: string): string {
  return `gaia-${id}`;
}

function isGaiaFileSelected(fileId: string): boolean {
  return localSelectedKeys.value.includes(fileKey(fileId));
}

function getSectionSelectionState(sectionId: string): 'none' | 'partial' | 'full' {
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- an empty/falsy value must use the fallback
  const sectionFiles = props.gaiaStore.sectionFiles[sectionId]?.files || [];
  if (sectionFiles.length === 0) return 'none';

  const selectedCount = sectionFiles.filter((file: GaiaFileMeta) =>
    isGaiaFileSelected(file.id ?? '')
  ).length;

  if (selectedCount === 0) return 'none';
  if (selectedCount === sectionFiles.length) return 'full';
  return 'partial';
}

const isSectionFullySelected = (sectionId: string): boolean =>
  getSectionSelectionState(sectionId) === 'full';
const isSectionPartiallySelected = (sectionId: string): boolean =>
  getSectionSelectionState(sectionId) === 'partial';

function toggleKeys(keys: string[]): void {
  const set = new Set(localSelectedKeys.value);
  const allIn = keys.every(k => set.has(k));

  if (allIn) {
    for (const k of keys) set.delete(k);
  } else {
    for (const k of keys) set.add(k);
  }

  localSelectedKeys.value = Array.from(set);
  emit('selection-change', localSelectedKeys.value);
}

async function handleSectionSelection(sectionId: string): Promise<void> {
  if (!expandedSections.value.has(sectionId)) {
    expandedSections.value.add(sectionId);
    await props.gaiaStore.fetchSectionFiles(sectionId);
  }

  let attempts = 0;
  while (!props.gaiaStore.sectionFiles[sectionId]?.files && attempts < 10) {
    await new Promise(resolve => setTimeout(resolve, 100));
    attempts++;
  }

  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- an empty/falsy value must use the fallback
  const sectionFiles = props.gaiaStore.sectionFiles[sectionId]?.files || [];
  const fileKeys = sectionFiles.map((file: GaiaFileMeta) => fileKey(file.id ?? ''));
  toggleKeys(fileKeys);
}

function handleGaiaFileSelection(fileId: string): void {
  toggleKeys([fileKey(fileId)]);
}

function loadMoreFiles(sectionId: string): void {
  props.gaiaStore.loadMoreFiles(sectionId);
}

function handleCollapseUpdate(names: string[]): void {
  const newExpandedSections = new Set(names);
  const previouslyExpanded = new Set(expandedSections.value);

  expandedSections.value = newExpandedSections;
  for (const name of names) {
    if (!previouslyExpanded.has(name)) {
      props.gaiaStore.fetchSectionFiles(name);
    }
  }
}

function clearAllSelections(): void {
  localSelectedKeys.value = [];
  emit('selection-change', []);
}

const cardBorder = gaiaNeutral.borderCard;
const dividerBorder = gaiaNeutral.borderDivider;
const hoverSurface = gaiaNeutral.surfaceHover;
const accentBorder = gaiaAccent.border;
const accentTint = gaiaAccent.tint;
</script>

<template>
  <div class="gaia-sections-container">
    <div class="files-header">
      <gaia-text size="body" weight="medium">
        Select files from GAIA Sections to run analysis
      </gaia-text>
      <n-flex v-if="props.totalSelectedCount > 0" align="center" :size="8">
        <gaia-text size="meta" tone="subtle">
          {{ props.totalSelectedCount }} item(s) selected
          <template v-if="hasGaiaFilesSelected">
            (GAIA files will also be imported to your Contract Vault)
          </template>
        </gaia-text>
        <gaia-button
          v-if="hasGaiaFilesSelected"
          size="tiny"
          type="error"
          ghost
          @click="clearAllSelections"
        >
          Clear
        </gaia-button>
      </n-flex>
    </div>
    <n-spin :show="props.gaiaStore.sectionGroups.loading">
      <n-flex v-if="props.gaiaStore.sectionGroups.error" vertical :size="8" style="padding: 16px">
        <gaia-banner :title="props.gaiaStore.sectionGroups.error" />
        <gaia-button size="small" @click="props.gaiaStore.fetchSectionGroups()">Retry</gaia-button>
      </n-flex>
      <div v-else-if="props.gaiaStore.sectionGroups.data.length > 0" class="gaia-tree-container">
        <div v-for="group in props.gaiaStore.sectionGroups.data" :key="group.id" class="gaia-group">
          <div class="group-header">
            <gaia-text size="body" weight="medium">{{ group.title }}</gaia-text>
            <gaia-text size="meta" tone="subtle">
              {{ group.updated_at ? new Date(group.updated_at).toLocaleDateString() : 'N/A' }}
            </gaia-text>
          </div>
          <div class="group-sections">
            <n-collapse
              :default-expanded-names="[]"
              :expanded-names="Array.from(expandedSections)"
              @update:expanded-names="handleCollapseUpdate"
            >
              <n-collapse-item
                v-for="section in group.sections"
                :key="section.id || `section-${Math.random()}`"
                :name="section.id || ''"
                :title="section.title || 'Untitled Section'"
                class="section-collapse-item"
              >
                <template #header>
                  <div class="section-header-content">
                    <gaia-checkbox
                      :checked="isSectionFullySelected(section.id || '')"
                      :indeterminate="isSectionPartiallySelected(section.id || '')"
                      @update:checked="handleSectionSelection(section.id || '')"
                      @click.stop
                    />
                    <gaia-text size="body" weight="medium">
                      {{ section.title || 'Untitled Section' }}
                    </gaia-text>
                  </div>
                </template>
                <template #header-extra>
                  <gaia-text size="micro" tone="subtle" style="margin-left: 8px">
                    {{
                      section.updated_at ? new Date(section.updated_at).toLocaleDateString() : 'N/A'
                    }}
                  </gaia-text>
                </template>

                <n-spin :show="props.gaiaStore.sectionFiles[section.id || '']?.loading">
                  <div
                    :class="{
                      'section-loading-wrapper':
                        props.gaiaStore.sectionFiles[section.id || '']?.loading,
                    }"
                  >
                    <div
                      v-if="props.gaiaStore.sectionFiles[section.id || '']?.error"
                      class="section-error"
                    >
                      <gaia-banner
                        :title="props.gaiaStore.sectionFiles[section.id || '']?.error || ''"
                      />
                    </div>
                    <div
                      v-else-if="props.gaiaStore.sectionFiles[section.id || '']?.files?.length"
                      class="files-list"
                    >
                      <div
                        v-for="file in props.gaiaStore.sectionFiles[section.id || '']?.files"
                        :key="file.id || `file-${Math.random()}`"
                        class="file-item"
                        :class="{ selected: isGaiaFileSelected(file.id || '') }"
                      >
                        <div class="file-content">
                          <gaia-checkbox
                            :checked="isGaiaFileSelected(file.id || '')"
                            class="file-checkbox"
                            @update:checked="handleGaiaFileSelection(file.id || '')"
                            @click.stop
                          />
                          <div class="file-info" @click="handleGaiaFileSelection(file.id || '')">
                            <gaia-text
                              size="body"
                              weight="medium"
                              :tone="isGaiaFileSelected(file.id || '') ? 'accent' : 'ink'"
                            >
                              {{ file.title || 'Untitled File' }}
                            </gaia-text>
                            <gaia-text size="micro" tone="subtle">{{ file.filename }}</gaia-text>
                          </div>
                          <gaia-text size="micro" tone="subtle" class="file-updated">
                            {{
                              file.updated_at
                                ? new Date(file.updated_at).toLocaleDateString()
                                : 'N/A'
                            }}
                          </gaia-text>
                        </div>
                      </div>

                      <div
                        v-if="props.gaiaStore.sectionFiles[section.id || '']?.hasMore"
                        class="load-more-container"
                      >
                        <gaia-button
                          size="small"
                          :loading="props.gaiaStore.sectionFiles[section.id || '']?.loading"
                          @click="loadMoreFiles(section.id || '')"
                        >
                          Load More Files
                        </gaia-button>
                        <gaia-text size="meta" tone="subtle">
                          Showing
                          {{ props.gaiaStore.sectionFiles[section.id || '']?.files?.length || 0 }}
                          of
                          {{ props.gaiaStore.sectionFiles[section.id || '']?.totalFiles || 0 }}
                          files
                        </gaia-text>
                      </div>
                    </div>
                    <gaia-empty-state
                      v-else-if="
                        !props.gaiaStore.sectionFiles[section.id || '']?.loading &&
                        !props.gaiaStore.sectionFiles[section.id || '']?.error
                      "
                      title="No files in this section"
                    />
                  </div>
                </n-spin>
              </n-collapse-item>
            </n-collapse>
          </div>
        </div>
      </div>
      <gaia-empty-state
        v-else-if="!props.gaiaStore.sectionGroups.loading"
        title="No GAIA section groups found"
      />
    </n-spin>
  </div>
</template>

<style scoped>
.gaia-sections-container {
  padding: 16px;
  min-height: 200px;
  flex: 1;
  overflow-y: auto;
  max-height: calc(90vh - 350px);
}

.files-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  flex-shrink: 0;
}

.gaia-tree-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 8px;
}

.gaia-group {
  border: 1px solid v-bind(cardBorder);
  border-radius: 8px;
  transition: box-shadow 0.2s ease;
}

.gaia-group:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  border-bottom: 1px solid v-bind(dividerBorder);
}

.section-header-content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.group-sections {
  padding: 16px;
}

.section-collapse-item {
  margin-bottom: 8px;
}

.section-collapse-item :deep(.n-collapse-item__header) {
  border-radius: 6px;
  transition: background-color 0.2s ease;
}

.section-collapse-item :deep(.n-collapse-item__header:hover) {
  background-color: v-bind(hoverSurface);
}

.section-collapse-item :deep(.n-collapse-item__content) {
  padding: 16px;
}

.section-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px;
}

.files-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.file-item {
  border-radius: 6px;
  transition: background-color 0.2s ease;
}

.file-item:hover {
  background-color: v-bind(hoverSurface);
}

.file-item.selected {
  background: v-bind(accentTint);
  box-shadow: inset 0 0 0 1px v-bind(accentBorder);
}

.file-content {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
}

.file-checkbox {
  flex-shrink: 0;
}

.file-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
}

.file-updated {
  margin-left: auto;
  flex-shrink: 0;
}

.load-more-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px;
  border-top: 1px solid v-bind(dividerBorder);
  margin-top: 8px;
}

.section-loading-wrapper {
  min-height: 120px;
}
</style>
